import { TabsHeader, TabsHeaderTitle, AuditLogsTable } from '@valiant/ui-shared'
import { useGetAuditLogsQuery } from '../../generated/graphql'
import { useMemo, useEffect } from 'react'
import { useDebounce } from '@valiant/ui-shared'
import { useQueryParams } from '@valiant/ui-shared'

interface LogsTabProps {
  userId?: string
  searchTerm?: string
}

export function LogsTab({ userId, searchTerm }: LogsTabProps) {
  const queryParams = useQueryParams()

  // Get state from query params with defaults
  const searchInput = searchTerm || queryParams.search || ''
  const pageSize = parseInt(queryParams.pageSize || '25', 10)
  const currentCursor = queryParams.cursor || null
  const direction = (queryParams.direction as 'forward' | 'backward') || 'forward'

  // Use the existing debounce hook
  const debouncedSearch = useDebounce(searchInput)

  // Reset pagination when search changes
  useEffect(() => {
    queryParams.updatePagination(pageSize.toString()) // Reset to first page
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [debouncedSearch, pageSize])

  const filter = useMemo(() => {
    interface FilterObject {
      userId?: string
      action?: string
    }

    const filterObj: FilterObject = {}
    if (userId) filterObj.userId = userId
    if (debouncedSearch.trim()) filterObj.action = debouncedSearch.trim()
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, userId])

  const pagination = useMemo(
    () => ({
      first: direction === 'forward' ? pageSize : undefined,
      after: direction === 'forward' ? currentCursor : undefined,
      last: direction === 'backward' ? pageSize : undefined,
      before: direction === 'backward' ? currentCursor : undefined,
    }),
    [direction, pageSize, currentCursor],
  )

  const [{ data: auditLogsData, fetching: _auditLogsLoading, error: _auditLogsError }] =
    useGetAuditLogsQuery({
      variables: { filter, pagination },
    })

  const auditLogs = auditLogsData?.getAuditLogs.nodes || []
  const pageInfo = auditLogsData?.getAuditLogs.pageInfo
  const totalCount = Number(auditLogsData?.getAuditLogs.totalCount || 0)
  // Pagination handlers
  const handleNextPage = () => {
    if (pageInfo?.hasNextPage && pageInfo.endCursor) {
      queryParams.updatePagination(pageSize.toString(), pageInfo.endCursor, 'forward')
    }
  }

  const handlePreviousPage = () => {
    if (pageInfo?.hasPreviousPage && pageInfo.startCursor) {
      queryParams.updatePagination(pageSize.toString(), pageInfo.startCursor, 'backward')
    }
  }

  const handlePageSizeChange = (newSize: number) => {
    queryParams.updatePagination(newSize.toString()) // Reset to first page
  }

  return (
    <div className="space-y-4">
      <TabsHeader>
        <TabsHeaderTitle>Activity Logs</TabsHeaderTitle>
      </TabsHeader>
      <AuditLogsTable
        logs={auditLogs}
        totalCount={totalCount}
        pageSize={pageSize}
        pageInfo={pageInfo}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
